import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useApi } from '../useApi';
import PlayerPickerModal from './PlayerPickerModal';
import PlayerTeeChip, { PlayerTeeSheet } from './PlayerTeeChip';
import { getTeeColor, teeNeedsOutline } from '../utils/teeColors';
import { abbreviateName } from '../utils/golf';
import { mixRounds, pairingCoverage, coverageLine } from '../utils/groupMixer';

const Step4_OrganizeGroups = ({ data, updateData, initialRound, onPickerOpenChange, selectedRound: controlledSelectedRound, onSelectedRoundChange }) => {
    const { authenticatedFetch } = useApi();

    // --- Round Navigation (Part 1) ---
    const availableRounds = useMemo(() => {
        const rounds = data.courses.map(c => c.sequence_number);
        return [...new Set(rounds)].sort((a, b) => a - b);
    }, [data.courses]);

    const roundCourseMap = useMemo(() => {
        return data.courses.reduce((map, c) => {
            map[c.sequence_number] = c;
            return map;
        }, {});
    }, [data.courses]);

    const [internalSelectedRound, setInternalSelectedRound] = useState(
        initialRound && availableRounds.includes(initialRound) ? initialRound : (availableRounds[0] || 1)
    );
    const isControlled = controlledSelectedRound != null;
    const selectedRound = isControlled ? controlledSelectedRound : internalSelectedRound;
    const setSelectedRound = isControlled ? (onSelectedRoundChange || (() => {})) : setInternalSelectedRound;
    const allPlayers = useMemo(() => data.players, [data.players]);
    const [groupSize, setGroupSize] = useState(() => {
        const fmt = data.format?.toLowerCase() || '';
        if (fmt === 'singles matchplay') return 2;
        if (fmt === 'pairs matchplay') return 4;
        return Math.min(allPlayers.length, 4) || 3;
    });

    // Format logic
    const formatLower = data.format?.toLowerCase() || '';
    const isSinglesMatchplay = formatLower === 'singles matchplay';
    const isPairsMatchplay = formatLower === 'pairs matchplay';
    const isMatchplay = isSinglesMatchplay || isPairsMatchplay;

    // Selected round data
    const selectedRoundData = useMemo(() =>
        data.groups.find(rg => rg.round_number === selectedRound),
    [data.groups, selectedRound]);

    const groups = useMemo(() => selectedRoundData?.groups || [], [selectedRoundData]);
    const pairingMode = selectedRoundData?.pairingMode || 'manual';
    const isRoundStarted = useMemo(() => selectedRoundData?.is_started || false, [selectedRoundData]);

    const [animationKey, setAnimationKey] = useState(0);

    // --- Mixed tees ---------------------------------------------------
    // The tees declared for THIS round at course selection. More than one and
    // every player gets a picker, because their Course Handicap comes from the
    // tee they actually play (`CH = HI x Slope/113 + (CR - Par)`). One tee and
    // none of this renders at all.
    const roundCourse = roundCourseMap[selectedRound];
    const teesInPlay = useMemo(() => {
        const ids = roundCourse?.tees_in_play || [];
        const byId = new Map((roundCourse?.tees || []).map(t => [t.id, t]));
        return ids.map(id => byId.get(id)).filter(Boolean);
    }, [roundCourse]);
    const hasMixedTees = teesInPlay.length > 1;
    const mainTeeId = roundCourse?.tee_id ?? null;

    const roundTeeAssignments = useMemo(
        () => (data.player_tees || {})[selectedRound] || {},
        [data.player_tees, selectedRound]
    );

    const setPlayerTee = useCallback((playerId, teeId) => {
        const all = { ...(data.player_tees || {}) };
        const forRound = { ...(all[selectedRound] || {}) };
        // The main tee is stored as "no assignment" so that changing the round's
        // tee later still moves everyone who was never given a specific one.
        if (teeId == null || teeId === mainTeeId) delete forRound[playerId];
        else forRound[playerId] = teeId;
        all[selectedRound] = forRound;
        updateData({ player_tees: all });
    }, [data.player_tees, selectedRound, mainTeeId, updateData]);

    // Which player's tee sheet is open, if any.
    const [teeSheetPlayer, setTeeSheetPlayer] = useState(null);

    // Gated on THIS round, never on the tournament. A multi-round event is
    // started as soon as one card in Round 1 has a score, and that says nothing
    // about Round 2 — which may not be drawn, let alone played. Locking the
    // picker tournament-wide left an organiser mid-event unable to move anyone
    // between tees for a round nobody had teed off in. The server draws the
    // same line: `set_player_tees` refuses a FINALISED card and writes through
    // to any other started round (tee, playing handicap, every hole rescored).
    const renderTeePicker = (player) => {
        if (!hasMixedTees) return null;
        return (
            <PlayerTeeChip
                teeName={teesInPlay.find(t => t.id === teeIdFor(player.id))?.name}
                compact
                disabled={isRoundStarted}
                onClick={() => setTeeSheetPlayer(player)}
            />
        );
    };

    const teeIdFor = useCallback(
        (playerId) => roundTeeAssignments[playerId] ?? mainTeeId ?? null,
        [roundTeeAssignments, mainTeeId]
    );

    // "T.Marchbank" is one unbreakable token, and on a 360px phone the two-up
    // grid cannot fit it beside a tee dot and a remove button. A <wbr/> after
    // the initial lets the surname drop to a second line INSTEAD of being cut
    // — the whole name, over two lines, beats nine letters of it over one.
    const breakableName = (name) => {
        const abbreviated = abbreviateName(name);
        const dot = abbreviated.indexOf('.');
        if (dot < 0) return abbreviated;
        return (
            <>
                {abbreviated.slice(0, dot + 1)}
                <wbr />
                {abbreviated.slice(dot + 1)}
            </>
        );
    };

    // Player picker modal state
    const [pickerOpen, setPickerOpen] = useState(false);
    const [activeGroupId, setActiveGroupId] = useState(null);

    // Notify parent when picker opens/closes (to hide footer button)
    useEffect(() => {
        onPickerOpenChange?.(pickerOpen);
    }, [pickerOpen, onPickerOpenChange]);

    // Sync groupSize on round/format change
    useEffect(() => {
        if (isSinglesMatchplay) { setGroupSize(2); return; }
        if (isPairsMatchplay) { setGroupSize(4); return; }
        if (groups.length > 0) {
            const maxInRound = Math.max(...groups.map(g => g.players?.length || 0));
            if (maxInRound > 0) setGroupSize(prev => Math.max(prev, maxInRound));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRound, isSinglesMatchplay, isPairsMatchplay]);

    // Unassigned players
    const unassignedPlayers = useMemo(() => {
        const assigned = new Set(groups.flatMap(g => (g.players || []).map(p => p.id)));
        return allPlayers
            .filter(p => !assigned.has(p.id))
            .sort((a, b) => {
                const lnA = a.name.trim().split(' ').pop().toLowerCase();
                const lnB = b.name.trim().split(' ').pop().toLowerCase();
                return lnA === lnB ? a.name.localeCompare(b.name) : (lnA < lnB ? -1 : 1);
            });
    }, [allPlayers, groups]);

    // --- Update helper ---
    const updateRoundGroups = useCallback((newGroups, mode) => {
        updateData({ round_number: selectedRound, groups: newGroups, pairingMode: mode !== undefined ? mode : pairingMode });
    }, [selectedRound, pairingMode, updateData]);

    // Auto-create empty groups when none exist, and auto-extend with another empty
    // group when the player roster grows past existing capacity (e.g. a 5th player was
    // added in Step 3 but the only group is already full at groupSize=4).
    useEffect(() => {
        if (isRoundStarted || allPlayers.length === 0) return;
        if (pairingMode === 'leaderboard') return;

        if (groups.length === 0) {
            const numGroups = Math.max(1, Math.ceil(allPlayers.length / groupSize));
            const emptyGroups = Array.from({ length: numGroups }, (_, i) => ({
                id: i + 1, name: `Group ${i + 1}`, players: []
            }));
            updateRoundGroups(emptyGroups, 'manual');
            return;
        }

        // Capacity of each existing group is max(groupSize, current player count) —
        // an over-sized group keeps its existing capacity rather than shrinking.
        const totalCapacity = groups.reduce(
            (sum, g) => sum + Math.max(groupSize, g.players?.length || 0),
            0
        );
        if (allPlayers.length > totalCapacity) {
            const extraNeeded = Math.ceil((allPlayers.length - totalCapacity) / groupSize);
            const maxId = Math.max(0, ...groups.map(g => g.id || 0));
            const extraGroups = Array.from({ length: extraNeeded }, (_, i) => ({
                id: maxId + i + 1,
                name: `Group ${maxId + i + 1}`,
                players: []
            }));
            updateRoundGroups([...groups, ...extraGroups]);
        }
    }, [allPlayers.length, groupSize, groups, isRoundStarted, pairingMode, updateRoundGroups]);

    // Defensive reconcile: drop any group member no longer on the roster (e.g. a
    // player removed elsewhere, or stale saved data). Without this a phantom
    // lingers in the tee sheet and the round can't start later. Skips started
    // rounds (locked) and is a no-op once clean, so it can't loop.
    useEffect(() => {
        if (isRoundStarted || groups.length === 0) return;
        const rosterIds = new Set(allPlayers.map(p => p.id));
        let changed = false;
        const cleaned = groups.map(g => {
            const kept = (g.players || []).filter(p => rosterIds.has(p.id));
            if (kept.length !== (g.players || []).length) { changed = true; return { ...g, players: kept }; }
            return g;
        });
        if (changed) updateRoundGroups(cleaned);
    }, [allPlayers, groups, isRoundStarted, updateRoundGroups]);

    // --- Handlers ---
    const handleAssignPlayerToGroup = useCallback((player, targetGroupId) => {
        if (isRoundStarted) return;
        if (groups.some(g => (g.players || []).some(p => p.id === player.id))) return;

        if (targetGroupId != null) {
            const targetGroup = groups.find(g => g.id === targetGroupId);
            if (targetGroup && (targetGroup.players?.length || 0) >= groupSize && !targetGroup.placeholder) return;

            const updated = groups.map(g =>
                g.id === targetGroupId
                    ? { ...g, players: [...(g.players || []), player], placeholder: false }
                    : g
            );
            updateRoundGroups(updated);
        } else {
            // No target group — assign to first available or create new
            let assigned = false;
            let updated = groups.map(g => {
                if (!assigned && (g.players?.length || 0) < groupSize && !g.placeholder) {
                    assigned = true;
                    return { ...g, players: [...(g.players || []), player], placeholder: false };
                }
                return g;
            });

            if (!assigned) {
                const newId = updated.length > 0 ? Math.max(...updated.map(g => g.id || 0)) + 1 : 1;
                updated = [...updated, { id: newId, name: `Group ${newId}`, players: [player] }];
            }

            updateRoundGroups(updated);
        }
        setPickerOpen(false);
        setActiveGroupId(null);
    }, [groups, groupSize, isRoundStarted, updateRoundGroups]);

    const handleRemovePlayer = useCallback((player, groupId) => {
        if (isRoundStarted) return;
        const updated = groups
            .map(g => g.id === groupId ? { ...g, players: (g.players || []).filter(p => p.id !== player.id) } : g)
            .filter(g => (g.players?.length || 0) > 0);
        updateRoundGroups(updated);
    }, [groups, isRoundStarted, updateRoundGroups]);

    const handleGroupSizeChange = useCallback((newSize) => {
        if (isMatchplay || isRoundStarted) return;
        if (isNaN(newSize) || newSize < 1) return;
        setGroupSize(newSize);
        updateRoundGroups([], 'manual');
    }, [isMatchplay, isRoundStarted, updateRoundGroups]);

    const handleSlotClick = useCallback((groupId) => {
        if (isRoundStarted) return;
        if (unassignedPlayers.length === 0) return;
        setActiveGroupId(groupId);
        setPickerOpen(true);
    }, [isRoundStarted, unassignedPlayers.length]);

    const handlePickerSelect = useCallback((player) => {
        if (activeGroupId !== null) {
            handleAssignPlayerToGroup(player, activeGroupId);
        }
    }, [activeGroupId, handleAssignPlayerToGroup]);


    // --- Generate Groups (Part 2) ---
    const generateRandomGroups = useCallback(() => {
        if (isRoundStarted) return;
        const shuffled = [...allPlayers].sort(() => Math.random() - 0.5);
        const newGroups = [];
        for (let i = 0; i < shuffled.length; i += groupSize) {
            const num = newGroups.length + 1;
            newGroups.push({ id: num, name: `Group ${num}`, players: shuffled.slice(i, i + groupSize) });
        }
        updateRoundGroups(newGroups, 'random');
        setAnimationKey(k => k + 1);
    }, [allPlayers, groupSize, isRoundStarted, updateRoundGroups]);

    // --- Mix every round ---
    // One draw for the whole competition, so that over its rounds everyone
    // plays with as many different people as the rounds allow. Three kinds of
    // round are NOT drawn:
    //   - started rounds — history the draw works around;
    //   - rounds you set yourself, with every player placed — KEPT as set and
    //     worked around the same way, so "Round 1 is the captains' draw, mix
    //     the rest" is: place Round 1, then Mix Every Round;
    //   - Leaderboard Pairing rounds, whose groups are the standings' to decide.
    // A drawn round (Random Draw or an earlier mix) is not "set by you", so it
    // is redrawn; so is a round only partly filled, after asking.
    const roundsForMix = useMemo(() => {
        const rosterIds = allPlayers.map(p => p.id);
        const fixed = [];
        const kept = [];
        const free = [];
        for (const roundNum of availableRounds) {
            const rd = data.groups.find(rg => rg.round_number === roundNum);
            const placed = new Set((rd?.groups || []).flatMap(g => (g.players || []).map(p => p.id)));
            const complete = rosterIds.length > 0 && rosterIds.every(id => placed.has(id));
            const drawn = rd?.pairingMode === 'mix' || rd?.pairingMode === 'random';
            if (rd?.is_started) fixed.push(rd);
            else if (rd?.pairingMode === 'leaderboard') continue;
            else if (complete && !drawn) { fixed.push(rd); kept.push(roundNum); }
            else free.push(roundNum);
        }
        return { fixed, kept, free };
    }, [availableRounds, data.groups, allPlayers]);

    const anyMixed = data.groups.some(rg => rg.pairingMode === 'mix');

    // `size` is passed rather than read from state when the Group Size control
    // is what asked: the new size is not in state until the next render.
    const mixAllRounds = useCallback((size = groupSize) => {
        if (isRoundStarted || allPlayers.length === 0 || size < 2) return;
        const { fixed, free } = roundsForMix;
        if (free.length === 0) return;

        // A round only PARTLY placed by hand is redrawn — there is no whole
        // draw in it to keep — and that happens off screen, so ask first. A
        // reversible change: a native confirm.
        const partSet = free.filter(roundNum => {
            if (roundNum === selectedRound) return false;
            const rd = data.groups.find(rg => rg.round_number === roundNum);
            return rd?.pairingMode !== 'mix' && rd?.pairingMode !== 'random'
                && (rd?.groups || []).some(g => (g.players?.length || 0) > 0);
        });
        if (partSet.length > 0) {
            const names = partSet.map(r => `Round ${r}`).join(', ');
            if (!window.confirm(`${names} ${partSet.length === 1 ? 'is' : 'are'} only partly set, so ${partSet.length === 1 ? 'it' : 'they'} will be redrawn. Carry on?`)) return;
        }

        const byId = new Map(allPlayers.map(p => [p.id, p]));
        const drawn = mixRounds({
            playerIds: allPlayers.map(p => p.id),
            groupSize: size,
            freeRounds: free.length,
            fixedRounds: fixed.map(rd => (rd.groups || []).map(g => (g.players || []).map(p => p.id))),
        });
        free.forEach((roundNum, i) => {
            updateData({
                round_number: roundNum,
                groups: drawn[i].map((ids, gi) => ({
                    id: gi + 1, name: `Group ${gi + 1}`, players: ids.map(id => byId.get(id)),
                })),
                pairingMode: 'mix',
            });
        });
        setAnimationKey(k => k + 1);
        return true;
    }, [isRoundStarted, allPlayers, roundsForMix, selectedRound, data.groups, groupSize, updateData]);

    // THE GROUP SIZE IS PART OF THE MIX. Once every round has been drawn
    // together, changing the size redraws every round together — clearing
    // only the round on screen (what the control does otherwise) left the
    // others in the old size, and the coverage line describing a draw that
    // no longer matched any of them.
    const changeGroupSize = (size) => {
        // On a round you set yourself, once the rest are mixed, the size is
        // the mixed rounds' to change — clearing yours would throw away the
        // very groups the mix was drawn around.
        const mixedAround = anyMixed && roundsForMix.kept.includes(selectedRound);
        if ((pairingMode === 'mix' || mixedAround) && !isMatchplay && size >= 2) {
            if (mixAllRounds(size)) setGroupSize(size);
            return;
        }
        handleGroupSizeChange(size);
    };

    // How the draw did, across every round that has groups — read off what is
    // on screen, so a player moved by hand afterwards is counted as moved.
    const mixCoverage = useMemo(() => {
        if (!anyMixed) return null;
        const rounds = availableRounds
            .map(roundNum => data.groups.find(rg => rg.round_number === roundNum))
            .filter(rd => rd && rd.pairingMode !== 'leaderboard')
            .map(rd => (rd.groups || []).map(g => (g.players || []).map(p => p.id)));
        return coverageLine(pairingCoverage(allPlayers.map(p => p.id), rounds, groupSize));
    }, [anyMixed, availableRounds, data.groups, allPlayers, groupSize]);

    // "Round 1 kept as you set it" — said whenever a mix has worked around a
    // round placed by hand, so nobody wonders why that one did not move.
    const keptLine = anyMixed && roundsForMix.kept.length > 0
        ? `${roundsForMix.kept.length === 1 ? 'Round' : 'Rounds'} ${
            roundsForMix.kept.join(', ')} kept as you set ${
            roundsForMix.kept.length === 1 ? 'it' : 'them'}`
        : null;

    const generateLeaderboardGroups = useCallback(async () => {
        if (isRoundStarted) return;

        if (data.id) {
            try {
                const res = await authenticatedFetch(`/tournaments/${data.id}/leaderboard_summary`);
                if (res.ok) {
                    const leaderboard = await res.json();
                    if (leaderboard.length > 0) {
                        const sorted = [...leaderboard].sort((a, b) => a.position - b.position);
                        const reversed = sorted.reverse();

                        const orderedPlayers = reversed
                            .map(entry => allPlayers.find(p => p.id === entry.player_id))
                            .filter(Boolean);

                        const lbIds = new Set(orderedPlayers.map(p => p.id));
                        const notInLb = allPlayers.filter(p => !lbIds.has(p.id));
                        const finalOrder = [...notInLb, ...orderedPlayers];

                        const newGroups = [];
                        for (let i = 0; i < finalOrder.length; i += groupSize) {
                            const num = newGroups.length + 1;
                            newGroups.push({ id: num, name: `Group ${num}`, players: finalOrder.slice(i, i + groupSize) });
                        }
                        updateRoundGroups(newGroups, 'leaderboard');
                        setAnimationKey(k => k + 1);
                        return;
                    }
                }
            } catch (err) {
                console.error('Failed to fetch leaderboard:', err);
            }
        }

        // No leaderboard data — show placeholder groups
        const numGroups = Math.max(1, Math.ceil(allPlayers.length / groupSize));
        const placeholders = Array.from({ length: numGroups }, (_, i) => ({
            id: i + 1, name: `Group ${i + 1}`, players: [], placeholder: true
        }));
        updateRoundGroups(placeholders, 'leaderboard');
        setAnimationKey(k => k + 1);
    }, [allPlayers, groupSize, isRoundStarted, data.id, authenticatedFetch, updateRoundGroups]);

    // --- Helpers ---
    const hasRealGroups = groups.length > 0 && !groups.every(g => g.placeholder);
    const isFinalGroupHighlight = (idx) => pairingMode === 'leaderboard' && idx === groups.length - 1 && groups.length > 1 && hasRealGroups;

    // --- Matchplay render ---
    const renderMatchplayGroup = (group) => {
        const splitIndex = isSinglesMatchplay ? 1 : 2;
        const team1 = (group.players || []).slice(0, splitIndex);
        const team2 = (group.players || []).slice(splitIndex);
        const team1Fillers = Array(Math.max(0, splitIndex - team1.length)).fill(null);
        const team2Fillers = Array(Math.max(0, splitIndex - team2.length)).fill(null);

        const renderEmptySlot = (key) => {
            const canAdd = !isRoundStarted && unassignedPlayers.length > 0;
            return (
                <div
                    key={key}
                    className={`matchplay-player matchplay-placeholder${canAdd ? ' matchplay-placeholder--clickable' : ''}`}
                    onClick={() => canAdd && handleSlotClick(group.id)}
                >
                    {canAdd ? '+ Add Player' : 'Empty Slot'}
                </div>
            );
        };

        return (
            <div className="matchplay-container">
                <div className="matchplay-team matchplay-team--red">
                    <h6>Red Team</h6>
                    {team1.map(p => (
                        <div key={p.id} className="matchplay-player">
                            <span title={p.name}>{abbreviateName(p.name)}</span>
                            {renderTeePicker(p)}
                            {!isRoundStarted && (
                                <button onClick={() => handleRemovePlayer(p, group.id)} className="group-card__remove-btn">&times;</button>
                            )}
                        </div>
                    ))}
                    {team1Fillers.map((_, i) => renderEmptySlot(`t1-empty-${i}`))}
                </div>
                <div className="matchplay-vs">VS</div>
                <div className="matchplay-team matchplay-team--blue">
                    <h6>Blue Team</h6>
                    {team2.map(p => (
                        <div key={p.id} className="matchplay-player">
                            <span title={p.name}>{abbreviateName(p.name)}</span>
                            {renderTeePicker(p)}
                            {!isRoundStarted && (
                                <button onClick={() => handleRemovePlayer(p, group.id)} className="group-card__remove-btn">&times;</button>
                            )}
                        </div>
                    ))}
                    {team2Fillers.map((_, i) => renderEmptySlot(`t2-empty-${i}`))}
                </div>
            </div>
        );
    };

    // Per-round completeness for the stepper checkmarks
    const isRoundComplete = useCallback((roundNum) => {
        const rd = data.groups.find(rg => rg.round_number === roundNum);
        if (!rd) return false;
        if (rd.pairingMode === 'leaderboard') return true;
        const rg = rd.groups || [];
        if (rg.length === 0) return false;
        const assignedIds = new Set(rg.flatMap(g => (g.players || []).map(p => p.id)));
        const rosterIds = new Set(allPlayers.map(p => p.id));
        const allAssigned = allPlayers.every(p => assignedIds.has(p.id));
        const everyGroupHasPlayer = rg.every(g => g.placeholder || (g.players?.length || 0) >= 1);
        // No phantom left behind: every slotted player is still on the roster.
        const onlyCurrentPlayers = rg.every(g => (g.players || []).every(p => rosterIds.has(p.id)));
        return allAssigned && everyGroupHasPlayer && onlyCurrentPlayers;
    }, [data.groups, allPlayers]);

    const currentRoundIndex = availableRounds.indexOf(selectedRound);
    const isMultiRound = availableRounds.length > 1;

    // Round stepper auto-scroll: center the current step when it changes or content overflows
    const stepperRef = useRef(null);
    const currentStepRef = useRef(null);
    const [stepperOverflows, setStepperOverflows] = useState(false);

    useEffect(() => {
        const container = stepperRef.current;
        const step = currentStepRef.current;
        if (!container) return;

        const sync = () => {
            const overflows = container.scrollWidth > container.clientWidth + 1;
            setStepperOverflows(overflows);
            if (overflows && step) {
                const target = step.offsetLeft + step.offsetWidth / 2 - container.clientWidth / 2;
                container.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
            }
        };

        sync();
        const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(sync) : null;
        ro?.observe(container);
        window.addEventListener('resize', sync);
        return () => {
            ro?.disconnect();
            window.removeEventListener('resize', sync);
        };
    }, [selectedRound, availableRounds.length]);

    return (
        <div className="wizard-step step4-groups">
            <h2>Player Groups</h2>

            {/* Round Stepper (multi-round only) */}
            {isMultiRound && (
                <>
                    <div className="round-stepper__heading">
                        Round {currentRoundIndex + 1} of {availableRounds.length}
                    </div>
                    <div
                        ref={stepperRef}
                        className={`round-stepper${stepperOverflows ? ' round-stepper--scrollable' : ''}`}
                    >
                        {availableRounds.map((roundNum, idx) => {
                            const rd = data.groups.find(rg => rg.round_number === roundNum);
                            const isLb = rd?.pairingMode === 'leaderboard';
                            const isCurrent = selectedRound === roundNum;
                            const isComplete = isRoundComplete(roundNum);
                            const prevComplete = idx > 0 && isRoundComplete(availableRounds[idx - 1]);
                            const courseName = roundCourseMap[roundNum]?.name;
                            const cls = `round-stepper__step${
                                isCurrent ? ' round-stepper__step--current' : ''
                            }${!isCurrent && isComplete ? ' round-stepper__step--complete' : ''}${
                                prevComplete ? ' round-stepper__step--prev-complete' : ''
                            }${rd?.is_started ? ' round-stepper__step--started' : ''}`;
                            return (
                                <button
                                    key={roundNum}
                                    ref={isCurrent ? currentStepRef : null}
                                    type="button"
                                    className={cls}
                                    onClick={() => setSelectedRound(roundNum)}
                                >
                                    <span className="round-stepper__marker">
                                        {!isCurrent && isComplete ? '✓' : (isLb ? '🏆' : roundNum)}
                                    </span>
                                    <span className="round-stepper__sublabel" title={courseName || `Round ${roundNum}`}>
                                        {courseName || `Round ${roundNum}`}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </>
            )}

            {/* Round Title */}
            <div className="round-title">
                <h3>Round {selectedRound} – {roundCourseMap[selectedRound]?.name || 'Unknown Course'}</h3>
                {hasMixedTees ? (
                    <span className="round-title__tee">
                        {teesInPlay.map(t => t.name).join(' · ')}
                    </span>
                ) : roundCourseMap[selectedRound]?.tee_name && (
                    <span className="round-title__tee">{roundCourseMap[selectedRound].tee_name}</span>
                )}
            </div>

            {hasMixedTees && !isRoundStarted && (
                <div className="mixed-tees-banner">
                    <span className="mixed-tees-banner__line">
                        <strong>{teesInPlay.length} tees in play this round.</strong>{' '}
                        Each player's handicap is worked out from the tee they
                        actually play, so set anyone who isn't on{' '}
                        <strong>{teesInPlay.find(t => t.id === mainTeeId)?.name || 'the main tee'}</strong>.
                    </span>
                    {/* A coloured circle reads as a label rather than a
                        control, so say plainly that it is tappable — and draw
                        the circle here so the eye can match it to the cards. */}
                    <span className="mixed-tees-banner__hint">
                        Tap a player's
                        <span className="mixed-tees-banner__chip">
                            <span
                                className={`player-tee-chip__dot${teeNeedsOutline(teesInPlay[0]?.name) ? ' player-tee-chip__dot--outlined' : ''}`}
                                style={{ backgroundColor: getTeeColor(teesInPlay[0]?.name) || '#ffffff' }}
                            />
                        </span>
                        to change their tee.
                    </span>
                </div>
            )}

            {/* Started Warning */}
            {isRoundStarted && (
                <div className="round-locked-banner">
                    Round {selectedRound} has already started. Group assignments are locked.
                </div>
            )}

            {/* Settings + Generate */}
            {!isRoundStarted && (
                <div className="group-organizer__settings">
                    <div className="settings-section">
                        <span className="settings-label">
                            Group Size {isMatchplay && <span className="settings-fixed">(Fixed)</span>}
                        </span>
                        <div className="segmented-control">
                            {[1, 2, 3, 4].map(size => (
                                <button
                                    key={size}
                                    type="button"
                                    className={`segmented-control__item ${groupSize === size ? 'active' : ''}`}
                                    onClick={() => changeGroupSize(size)}
                                    disabled={isMatchplay || isRoundStarted}
                                >{size}</button>
                            ))}
                        </div>
                    </div>

                    <div className="settings-section">
                        <span className="settings-label">Generate Groups</span>
                        <div className="generate-groups__cards">
                            {isMatchplay && (
                                <button
                                    title="Tap slots to place players"
                                    className={`generate-card ${pairingMode === 'manual' ? 'generate-card--active' : ''}`}
                                    onClick={() => {
                                        if (isRoundStarted) return;
                                        const numGroups = Math.max(1, Math.ceil(allPlayers.length / groupSize));
                                        const emptyGroups = Array.from({ length: numGroups }, (_, i) => ({
                                            id: i + 1, name: `Group ${i + 1}`, players: []
                                        }));
                                        updateRoundGroups(emptyGroups, 'manual');
                                        setAnimationKey(k => k + 1);
                                    }}
                                >
                                    <span className="generate-card__icon">✏️</span>
                                    <div className="generate-card__text">
                                        <span className="generate-card__title">Manual Assignment</span>
                                    </div>
                                </button>
                            )}
                            <button
                                title="Randomise players across groups"
                                className={`generate-card ${pairingMode === 'random' ? 'generate-card--active' : ''}`}
                                onClick={generateRandomGroups}
                            >
                                <span className="generate-card__icon">🎲</span>
                                <div className="generate-card__text">
                                    <span className="generate-card__title">Random Draw</span>
                                </div>
                            </button>
                            {isMultiRound && !isMatchplay && groupSize >= 2 && pairingMode !== 'leaderboard' && roundsForMix.free.length > 0 && (
                                <button
                                    title="Draw every round so everyone plays with everyone they can"
                                    className={`generate-card ${pairingMode === 'mix' ? 'generate-card--active' : ''}`}
                                    onClick={() => mixAllRounds()}
                                >
                                    <span className="generate-card__icon">🔀</span>
                                    <div className="generate-card__text">
                                        <span className="generate-card__title">Mix Every Round</span>
                                    </div>
                                </button>
                            )}
                            {selectedRound > 1 && (
                                <button
                                    title="Groups based on standings"
                                    className={`generate-card ${pairingMode === 'leaderboard' ? 'generate-card--active' : ''}`}
                                    onClick={generateLeaderboardGroups}
                                >
                                    <span className="generate-card__icon">🏆</span>
                                    <div className="generate-card__text">
                                        <span className="generate-card__title">Leaderboard Pairing</span>
                                    </div>
                                </button>
                            )}
                        </div>
                        {mixCoverage && (
                            <p className="mix-coverage">
                                {keptLine ? <>{keptLine}<br /></> : null}
                                {mixCoverage}
                            </p>
                        )}
                    </div>
                </div>
            )}

            {/* Group Cards */}
            <div className="group-cards" key={`groups-${animationKey}`}>
                {groups.length === 0 ? (
                    <p className="group-cards__empty">{isRoundStarted ? 'No groups assigned' : 'Add players to create groups'}</p>
                ) : (
                    groups.map((group, idx) => {
                        const isFinal = isFinalGroupHighlight(idx);
                        const filled = group.players?.length || 0;
                        const isFull = filled >= groupSize;
                        const isPlaceholder = group.placeholder;
                        const playerOffset = groups.slice(0, idx).reduce((sum, g) => sum + (g.players?.length || 0), 0);

                        return (
                            <div
                                key={group.id}
                                className={`group-card ${isFinal ? 'group-card--final' : ''} ${isRoundStarted ? 'group-card--locked' : ''}`}
                            >
                                <div className="group-card__header">
                                    <span className="group-card__name">
                                        {isFinal && <span className="group-card__trophy">🏆 </span>}
                                        {isFinal ? 'FINAL GROUP' : group.name.toUpperCase()}
                                    </span>
                                    <span className={`group-card__badge ${isFull ? 'group-card__badge--full' : ''}`}>
                                        {isRoundStarted ? '🔒' : (isFull ? '✓ FULL' : `${filled} / ${groupSize}`)}
                                    </span>
                                </div>

                                {isPlaceholder ? (
                                    <div className="group-card__placeholder-body">
                                        <span>(To be generated)</span>
                                    </div>
                                ) : isMatchplay ? (
                                    renderMatchplayGroup(group)
                                ) : (
                                    <div className={`group-card__body ${groupSize >= 3 ? 'group-card__body--grid' : ''}`}>
                                        {(group.players || []).map((player, pIdx) => (
                                            <div
                                                key={player.id}
                                                className={`group-card__player-card ${animationKey > 0 ? 'group-card__player-card--animating' : ''}`}
                                                style={animationKey > 0 ? { animationDelay: `${(playerOffset + pIdx) * 40}ms` } : {}}
                                            >
                                                {/* "B.Bailey" rather than "Baz
                                                    Bailey": two cards to a row
                                                    leaves under 80px for a name,
                                                    and the surname is what
                                                    identifies a player. */}
                                                <span className="group-card__player-name" title={player.name}>
                                                    {breakableName(player.name)}
                                                </span>
                                                <span className="group-card__hcp-badge">HCP {player.handicap}</span>
                                                {renderTeePicker(player)}
                                                {!isRoundStarted && (
                                                    <button
                                                        onClick={() => handleRemovePlayer(player, group.id)}
                                                        className="group-card__remove-btn"
                                                    >&times;</button>
                                                )}
                                            </div>
                                        ))}
                                        {/* Empty slots */}
                                        {!isRoundStarted && !isPlaceholder && Array.from({ length: Math.max(0, groupSize - filled) }).map((_, i) => (
                                            <div key={`slot-${i}`} className="group-card__slot-wrapper">
                                                <div
                                                    className="group-card__empty-slot"
                                                    onClick={() => handleSlotClick(group.id)}
                                                >
                                                    {unassignedPlayers.length > 0 ? (
                                                        <>
                                                            <span className="group-card__empty-slot-title">+ Add Player</span>
                                                            <span className="group-card__empty-slot-sub">Tap to select</span>
                                                        </>
                                                    ) : 'No players available'}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}

            </div>

            {/* Unassigned players summary */}
            {!isRoundStarted && unassignedPlayers.length > 0 && (
                <p className="unassigned-hint">
                    {unassignedPlayers.length} player{unassignedPlayers.length !== 1 ? 's' : ''} not yet assigned — tap an empty slot above to place them
                </p>
            )}

            {/* All assigned indicator */}
            {!isRoundStarted && unassignedPlayers.length === 0 && groups.length > 0 && hasRealGroups && (
                <p className="all-assigned-message">All players assigned!</p>
            )}

            <PlayerPickerModal
                open={pickerOpen}
                players={unassignedPlayers}
                onClose={() => { setPickerOpen(false); setActiveGroupId(null); }}
                onSelect={handlePickerSelect}
            />

            <PlayerTeeSheet
                isOpen={!!teeSheetPlayer}
                playerName={teeSheetPlayer?.name}
                tees={teesInPlay}
                selectedTeeId={teeSheetPlayer ? teeIdFor(teeSheetPlayer.id) : null}
                onSelect={(teeId) => setPlayerTee(teeSheetPlayer.id, teeId)}
                onClose={() => setTeeSheetPlayer(null)}
            />
        </div>
    );
};

export default Step4_OrganizeGroups;
